import {
  Clock3,
  CalendarDays,
  LogIn,
  TimerReset,
  Flame,
} from 'lucide-react';

import { LoggedHoursKpiResponse } from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  kpi?: LoggedHoursKpiResponse | null;
}

/* ---------------- Types ---------------- */

interface KpiCard {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ElementType;
  bg: string;
  color: string;
  subtitleColor: string;
}

/* ---------------- Helpers ---------------- */

const renderDuration = (value?: string | number) => {
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    value === '--'
  ) {
    return (
      <span className="text-[16px] font-bold text-[#101828]">
        --
      </span>
    );
  }

  const text = String(value).trim();

  // HH:MM:SS or HH:MM
  if (text.includes(':')) {
    const [h = '00', m = '00'] = text.split(':');

    return (
      <div className="flex items-end gap-[3px] leading-none">
        <span className="text-[18px] font-bold text-[#101828]">
          {Number(h).toString().padStart(2, '0')}
        </span>

        <span className="mb-[2px] text-[12px] text-[#667085]">
          h
        </span>

        <span className="text-[18px] font-bold text-[#101828]">
          {Number(m).toString().padStart(2, '0')}
        </span>

        <span className="mb-[2px] text-[12px] text-[#667085]">
          m
        </span>
      </div>
    );
  }

  // 44h 17m / 44h
  const match = text.match(/(\d+)\s*h\s*(\d+)?\s*m?/i);

  if (match) {
    return (
      <div className="flex items-end gap-[3px] leading-none">
        <span className="text-[18px] font-bold text-[#101828]">
          {match[1]}
        </span>

        <span className="mb-[2px] text-[12px] text-[#667085]">
          h
        </span>

        {match[2] && (
          <>
            <span className="text-[18px] font-bold text-[#101828]">
              {match[2]}
            </span>

            <span className="mb-[2px] text-[12px] text-[#667085]">
              m
            </span>
          </>
        )}
      </div>
    );
  }

  // Normal value
  return (
    <span className="text-[18px] font-bold text-[#101828]">
      {text}
    </span>
  );
};

/* ---------------- Component ---------------- */

const LoggedHoursKpiCards = ({ kpi }: Props) => {
  const cards: KpiCard[] = [
    {
      title: 'Logged Hours',
      value: kpi?.loggedToday ?? '--',
      subtitle:
        kpi?.loggedVarianceMinutes !== undefined
          ? `${
              kpi.loggedVarianceMinutes >= 0 ? '+' : ''
            }${kpi.loggedVarianceMinutes} mins vs yesterday`
          : '',
      icon: Clock3,
      bg: 'bg-[#EEF4FF]',
      color: 'text-[#3B82F6]',
      subtitleColor: 'text-[#16A34A]',
    },

    {
      title: 'WTD Hours',
      value: kpi?.wtdHours ?? '--',
      subtitle:
        kpi?.wtdPercentage !== undefined
          ? `of 45h target · ${kpi.wtdPercentage}%`
          : '',
      icon: CalendarDays,
      bg: 'bg-[#ECFDF3]',
      color: 'text-[#10B981]',
      subtitleColor: 'text-[#16A34A]',
    },

    {
      title: 'Login Time',
      value: kpi?.loginTime ?? '--',
      subtitle: 'Expected logout 18:00',
      icon: LogIn,
      bg: 'bg-[#EEF2FF]',
      color: 'text-[#7C3AED]',
      subtitleColor: 'text-[#667085]',
    },

    // {
    //   title: 'Overtime',
    //   value: kpi?.overtimeHours ?? '--',
    //   subtitle: 'Above 8h shift baseline',
    //   icon: TimerReset,
    //   bg: 'bg-[#FFF7ED]',
    //   color: 'text-[#EA580C]',
    //   subtitleColor: 'text-[#667085]',
    // },

    // {
    //   title: 'Streak',
    //   value: kpi?.streak ?? '--',
    //   subtitle: 'Days target met in a row',
    //   icon: Flame,
    //   bg: 'bg-[#FDF2F8]',
    //   color: 'text-[#DB2777]',
    //   subtitleColor: 'text-[#667085]',
    // },
  ];

  /*
   * Dynamically calculate columns.
   *
   * 5 cards -> 5 columns
   * 4 cards -> 4 columns
   * 3 cards -> 3 columns
   * 2 cards -> 2 columns
   * 1 card  -> 1 column
   */
  const desktopColumns = `repeat(${cards.length}, minmax(0, 1fr))`;

  return (
    <div className="w-full">
      {/* Mobile / Tablet */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <KpiCardItem
              key={card.title}
              card={card}
            />
          );
        })}
      </div>

      {/* Desktop */}
      <div
        className="hidden w-full gap-3 lg:grid"
        style={{
          gridTemplateColumns: desktopColumns,
        }}
      >
        {cards.map((card) => {
          return (
            <KpiCardItem
              key={card.title}
              card={card}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- Card ---------------- */

interface KpiCardItemProps {
  card: KpiCard;
}

const KpiCardItem = ({ card }: KpiCardItemProps) => {
  const Icon = card.icon;

  return (
    <div
      className="
        min-h-[82px]
        w-full
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        px-[16px]
        py-[12px]
        shadow-sm
      "
    >
      <div className="flex h-full items-start gap-3">
        {/* Icon */}
        <div
          className={`
            flex
            h-[30px]
            w-[30px]
            shrink-0
            items-center
            justify-center
            rounded-full
            ${card.bg}
          `}
        >
          <Icon
            size={16}
            className={card.color}
            strokeWidth={2.2}
          />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <h4 className="text-[13px] font-semibold leading-none text-[#101828]">
            {card.title}
          </h4>

          <div className="mt-[5px]">
            {card.title === 'Streak' ? (
              <span className="text-[18px] font-bold leading-none text-[#101828]">
                {card.value}
              </span>
            ) : (
              renderDuration(card.value)
            )}
          </div>

          {card.subtitle && (
            <p
              className={`
                mt-[5px]
                text-[11px]
                font-medium
                leading-none
                ${card.subtitleColor}
              `}
            >
              {card.subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoggedHoursKpiCards;